import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../shared/permissions';
import {
    type CreateMobilityAdditionalInfoMutation,
    type CreateMobilityAdditionalInfoMutationVariables,
    CreateMobilityAdditionalInfoDocument,
} from '../../../api/mutations/createMobilityAdditionalInfo';
import {
    type CreateMobilityAddonMutation,
    type CreateMobilityAddonMutationVariables,
    CreateMobilityAddonDocument,
} from '../../../api/mutations/createMobilityAddon';
import { MobilityKind, type MobilityAddonSetting, type MobilityAdditionalInfoSetting } from '../../../api/types';
import useLoadingButton from '../../../components/button/useLoadingButton';
import { useAccount } from '../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import Form from '../../../components/fields/Form';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useHandleError from '../../../utilities/useHandleError';
import { CreateMobilityForm, type CreateMobilityFormValues } from '../MobilityDetailPage/MobilityForm';
import useMobilityFormValidator from '../MobilityDetailPage/MobilityForm/useMobilityFormValidator';

const defaultValues = {
    kind: MobilityKind.Addon,
    moduleId: null,
    referenceName: null,
    title: { defaultValue: '', overrides: [] },
    isChargeable: false,
    options: [],
    detail: [],
    stockIds: [],
    isActive: true,
    order: 1,
};

export const cleanupMobilityAddOn = (addon: MobilityAddonSetting): MobilityAddonSetting => {
    const { options } = addon;
    // non-chargeable options should have no price
    const updatedOptions = options.map(option => (addon.isChargeable ? option : omit(['price'], option)));

    // omit changes the field to be partial
    return {
        ...omit(['detail', 'kind'], addon),
        options: updatedOptions,
    } as MobilityAddonSetting;
};

const AddMobilityPage = () => {
    const { t } = useTranslation(['mobilityDetails']);
    const navigate = useNavigate();
    const validate = useMobilityFormValidator();
    const { setLoading, LoadingButton } = useLoadingButton();
    const company = useCompany(true);
    const { permissions: accountPermissions } = useAccount();
    const hasPermission =
        hasPermissions(company?.permissions, [permissionKind.viewMobilities]) &&
        hasPermissions(accountPermissions, [permissionKind.updateMobility]);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<CreateMobilityFormValues>(
        async values => {
            const { moduleId, ...settings } = values;
            setLoading(true);
            // submitting message
            message.loading({
                content: t('mobilityDetails:messages.createSubmitting'),
                key: 'primary',
                duration: 0,
            });

            if (settings.kind === MobilityKind.Addon) {
                await apolloClient
                    .mutate<CreateMobilityAddonMutation, CreateMobilityAddonMutationVariables>({
                        mutation: CreateMobilityAddonDocument,
                        variables: {
                            setting: cleanupMobilityAddOn(settings),
                            moduleId,
                        },
                    })
                    .finally(() => {
                        setLoading(false);
                        message.destroy('primary');
                    });
            } else if (settings.kind === MobilityKind.AdditionalInfo) {
                await apolloClient
                    .mutate<CreateMobilityAdditionalInfoMutation, CreateMobilityAdditionalInfoMutationVariables>({
                        mutation: CreateMobilityAdditionalInfoDocument,
                        variables: {
                            setting: omit(
                                ['options', 'kind', 'isChargeable', 'referenceName'],
                                settings
                            ) as MobilityAdditionalInfoSetting,
                            moduleId,
                        },
                    })
                    .finally(() => {
                        setLoading(false);
                        message.destroy('primary');
                    });
            }

            // inform about success
            message.success({
                content: t('mobilityDetails:messages.createSuccessful'),
                key: 'primary',
            });
            // then go back to list
            navigate(`/admin/mobilityoptions`);
        },
        [apolloClient, navigate, setLoading, t]
    );

    return (
        <ConsolePageWithHeader
            footer={
                hasPermission && [
                    <LoadingButton key="submit" form="mobilityForm" htmlType="submit" type="primary">
                        {t('mobilityDetails:actions.create')}
                    </LoadingButton>,
                ]
            }
            onBack={() => navigate('/admin/mobilityoptions')}
            title={t('mobilityDetails:addTitle')}
        >
            <Formik initialValues={defaultValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form id="mobilityForm" name="mobilityForm" onSubmitCapture={handleSubmit}>
                        <CreateMobilityForm />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddMobilityPage;
